/**
 * Guía: Tickets de Soporte.
 *
 * Contenido derivado del capítulo **08** del manual: §0 (de dónde vienen),
 * §3 (estados, prioridades y categorías), §4.1 (el aislamiento del agente),
 * §6 (notas internas), §7 (el ciclo) y §5.2 (las respuestas rápidas).
 *
 * Se eligió como primera guía operativa porque es donde el equipo de soporte
 * pasa el día y porque concentra las tres reglas que más caro salen si no se
 * conocen: **una nota interna no es una respuesta**, **responder en público
 * sella la primera respuesta** y **resolver exige descripción y es el único
 * camino a "Resuelto"**.
 *
 * Los pasos sobre el modal de detalle van sin objetivo, centrados: abrirlo
 * exige pulsar un ticket, y el motor no pulsa por nadie — además de que una
 * bandeja vacía no tiene ninguno que pulsar.
 */

import type { TourFlow } from '../types';

const TICKETS_PATH = '/support-tickets';

export const c08TicketsDeSoporte: TourFlow = {
  id: 'admin.support-tickets',
  chapter: '08',
  title: 'Tickets de soporte',
  summary:
    'De dónde llegan, cómo se asignan, qué sella la primera respuesta y cómo se cierran.',
  href: TICKETS_PATH,
  requiresCountry: true,
  estimatedMin: 4,
  steps: [
    {
      id: 'intro',
      title: 'La bandeja del equipo de soporte',
      body:
        'Aquí llegan los problemas de clientes y de comercios. Los tickets no se crean en ' +
        'este panel: nacen en la app del cliente, en el dashboard del comercio o en el de ' +
        'la sucursal. Tú los recibes, los repartes, los respondes y los cierras.',
      route: TICKETS_PATH,
    },
    {
      id: 'country',
      title: 'Sólo ves los de tu país',
      body:
        'La bandeja está acotada al país seleccionado en el menú. Un ticket se ancla al ' +
        'país por la tienda que tocó, por la empresa que lo abrió o por el país del ' +
        'cliente. Si buscas uno concreto y no aparece, revisa primero qué país tienes puesto.',
    },
    {
      id: 'agent-scope',
      title: 'Aquí sólo están los tuyos',
      body:
        'Como agente ves y trabajas únicamente los tickets que tienes asignados — por eso ' +
        'la pantalla se llama «Mis Tickets». No es un filtro que puedas quitar: el ' +
        'servidor aplica la misma regla, así que un ticket que no sea tuyo no se abre ni ' +
        'con su enlace directo.',
      roles: ['SUPPORT_AGENT'],
    },
    {
      id: 'stats',
      title: 'El pulso de la bandeja',
      body:
        'Estas tarjetas cuentan los tickets por estado y prioridad. Se actualizan solas: ' +
        'la pantalla escucha en tiempo real los tickets que entran, los que cambian y los ' +
        'mensajes nuevos, así que no hace falta recargar para enterarse.',
      target: 'admin.tickets.stats',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'tabs',
      title: 'Los siete estados',
      body:
        'Abierto (recién llegado), En progreso (alguien lo está atendiendo), En espera del ' +
        'cliente o del comercio, Resuelto, Cerrado y Reabierto. Los dos de espera no los ' +
        'eliges tú: el ticket pasa solo al que corresponda cuando respondes en público.',
      target: 'admin.tickets.tabs',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'filters',
      title: 'Filtrar y encontrar',
      body:
        'Busca por asunto o descripción, y acota por prioridad, categoría, tienda o agente. ' +
        '«Sin asignar» es el filtro para repartir trabajo: son los tickets que todavía no ' +
        'tienen dueño.',
      target: 'admin.tickets.filters',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'priority-order',
      title: 'Lo crítico sube solo',
      body:
        'La lista ordena por prioridad y, dentro de cada una, por fecha. Un ticket crítico ' +
        'aparece arriba sin que nadie lo mueva, y además dispara la alerta del panel de ' +
        'Monitoreo Operativo. La categoría, en cambio, la elige quien abre el ticket: no se ' +
        'cambia desde aquí.',
      target: 'admin.tickets.table',
      placement: 'top',
      optionalTarget: true,
    },
    {
      id: 'assign',
      title: 'Repartir el trabajo',
      body:
        'Asignar un ticket es tuyo y del Super Admin: un agente no reparte. Al asignar uno ' +
        'que estaba abierto, pasa automáticamente a «En progreso» y queda una nota interna ' +
        'de quién lo movió. Puedes asignar varios a la vez seleccionándolos en la tabla.',
      roles: ['SUPER_ADMIN', 'SUPPORT_LEAD'],
    },
    {
      id: 'detail',
      title: 'El trabajo ocurre en el detalle',
      body:
        'Al abrir un ticket verás la conversación completa, la caja de respuesta y, al ' +
        'lado, el estado, la prioridad, el agente asignado y el contexto: quién lo abrió, ' +
        'qué tienda y qué pedido. Desde ahí se asigna, se resuelve y se reabre.',
    },
    {
      id: 'internal-notes',
      title: 'Una nota interna no es una respuesta',
      body:
        'La caja de respuesta lleva un interruptor de nota interna. Con él activado el ' +
        'mensaje queda sólo para el equipo: no le llega a quien abrió el ticket, no cambia ' +
        'el estado, no le notifica y no cuenta como primera respuesta. Con él apagado, el ' +
        'mensaje sale. Comprueba el interruptor antes de enviar.',
    },
    {
      id: 'public-reply',
      title: 'Responder en público hace tres cosas',
      body:
        'Mueve el ticket a la espera que corresponda —del cliente o del comercio—, avisa a ' +
        'quien lo abrió, y sella la primera respuesta, que es lo que se mide. El sistema ' +
        'además escribe solo una nota interna cada vez que alguien asigna, cambia el estado ' +
        'o resuelve, para que el historial quede sin ensuciar la conversación del cliente.',
    },
    {
      id: 'resolve',
      title: 'Resolver pide una descripción',
      body:
        'Es el único camino a «Resuelto»: el selector de estado no lo ofrece. Hay que ' +
        'escribir cómo se resolvió, y eso se guarda con el ticket y se le avisa a quien lo ' +
        'abrió. Después, el cliente o el comercio pueden cerrarlo o reabrirlo; tú también ' +
        'puedes reabrirlo desde el detalle.',
    },
    {
      id: 'quick-replies',
      title: 'Tus respuestas rápidas son tuyas',
      body:
        'Las plantillas de la caja de respuesta se guardan en este navegador y en tu ' +
        'cuenta: no se comparten con el equipo y no te siguen si cambias de equipo o de ' +
        'navegador. Si tienes una plantilla que le sirve a todos, pásala por otro canal ' +
        'hasta que se puedan compartir.',
    },
    {
      id: 'export',
      title: 'Llevarte la bandeja',
      body:
        'Exporta a CSV lo que tengas filtrado en pantalla. Es la vía para revisar volúmenes ' +
        'o preparar un informe fuera del panel.',
      target: 'admin.tickets.export',
      placement: 'bottom',
      optionalTarget: true,
    },
  ],
};
